import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE,
  buildListAdminProspectLetterTemplatesRequest,
  listAdminProspectLetterTemplates,
} from '../../services/admin/prospectLetterTemplatesService.js';
import { formatAdminDate } from './adminUserDisplay.js';
import {
  ADMIN_DATE_PLACEHOLDER,
  adminProspectLetterTemplateErrorKey,
  adminProspectLetterTemplateStatusBadgeClass,
  adminProspectLetterTemplateStatusLabel,
  classifyAdminCallableError,
} from './adminProspectLetterTemplateDisplay.js';
import AdminDuplicateLetterTemplateDialog from './AdminDuplicateLetterTemplateDialog.jsx';
import AdminArchiveLetterTemplateDialog from './AdminArchiveLetterTemplateDialog.jsx';

export default function AdminProspectLetterTemplatesPage() {
  const { t, language } = useLanguage();
  const { impersonation } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [templates, setTemplates] = useState([]);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [tokenStack, setTokenStack] = useState([]);
  const [currentToken, setCurrentToken] = useState(null);
  const [errorKind, setErrorKind] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const requestSeqRef = useRef(0);

  const loadList = async (pageToken, { paging = false, nextStack } = {}) => {
    const requestId = ++requestSeqRef.current;
    setStatus(paging ? 'paging' : 'loading');
    setErrorKind(null);
    try {
      const data = await listAdminProspectLetterTemplates(
        buildListAdminProspectLetterTemplatesRequest({
          pageSize: ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE,
          pageToken: pageToken || null,
        }),
      );
      if (requestId !== requestSeqRef.current) return;
      const rows = Array.isArray(data?.templates) ? data.templates : [];
      setTemplates(rows);
      setNextPageToken(data?.nextPageToken || null);
      setCurrentToken(pageToken || null);
      if (Array.isArray(nextStack)) setTokenStack(nextStack);
      else if (!paging) setTokenStack([]);
      setStatus(rows.length === 0 ? 'empty' : 'ready');
    } catch (err) {
      if (requestId !== requestSeqRef.current) return;
      setTemplates([]);
      setNextPageToken(null);
      setErrorKind(classifyAdminCallableError(err));
      setStatus('error');
    }
  };

  useEffect(() => {
    loadList(undefined);
  }, []);

  const handleNext = () => {
    if (!nextPageToken) return;
    loadList(nextPageToken, { paging: true, nextStack: [...tokenStack, currentToken] });
  };

  const handlePrevious = () => {
    if (tokenStack.length === 0) return;
    const stack = [...tokenStack];
    const prev = stack.pop();
    loadList(prev, { paging: true, nextStack: stack });
  };

  const handleDuplicateSuccess = (templateId) => {
    setDuplicateTarget(null);
    navigate(`/admin/prospection/letter-templates/${templateId}`);
  };

  const impersonating = impersonation?.active === true;

  const handleArchiveSuccess = () => {
    setArchiveTarget(null);
    loadList(currentToken, { paging: status === 'paging' });
  };

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-2">
          <FileText className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" aria-hidden="true" />
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-slate-900">
              {t('admin.prospection.letterTemplate.list.title')}
            </h1>
            <p className="mt-1 text-sm leading-snug text-slate-600">
              {t('admin.prospection.letterTemplate.list.subtitle')}
            </p>
          </div>
        </div>
        {impersonating ? (
          <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
            {t('admin.prospection.letterTemplate.impersonationBlocked')}
          </p>
        ) : (
          <Link
            to="/admin/prospection/letter-templates/new"
            className="inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 sm:w-auto"
          >
            {t('admin.prospection.letterTemplate.list.newCta')}
          </Link>
        )}
      </div>

      {status === 'loading' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.letterTemplate.list.loading')}
        </p>
      ) : null}
      {status === 'paging' ? (
        <p className="text-sm text-slate-500" aria-live="polite">
          {t('admin.prospection.letterTemplate.list.paging')}
        </p>
      ) : null}
      {status === 'error' ? (
        <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" role="alert">
          {t(adminProspectLetterTemplateErrorKey(errorKind))}
        </p>
      ) : null}
      {status === 'empty' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
          <p>{t('admin.prospection.letterTemplate.list.empty')}</p>
          {!impersonating ? (
            <Link
              to="/admin/prospection/letter-templates/new"
              className="mt-3 inline-flex min-h-11 items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              {t('admin.prospection.letterTemplate.list.newCta')}
            </Link>
          ) : null}
        </div>
      ) : null}

      {templates.length > 0 && (status === 'ready' || status === 'paging') ? (
        <>
          <div className="hidden overflow-hidden rounded-xl border border-slate-200 bg-white md:block">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.name')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.status')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.version')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.variables')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.qr')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.updatedAt')}</th>
                  <th className="px-3 py-2">{t('admin.prospection.letterTemplate.field.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {templates.map((item) => (
                  <tr key={item.templateId} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium text-slate-900">{item.name}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${adminProspectLetterTemplateStatusBadgeClass(item.status)}`}
                      >
                        {adminProspectLetterTemplateStatusLabel(item.status, t)}
                      </span>
                    </td>
                    <td className="px-3 py-2">{item.currentVersion ?? ADMIN_DATE_PLACEHOLDER}</td>
                    <td className="px-3 py-2">
                      {Array.isArray(item.currentPlaceholderKeys) ? item.currentPlaceholderKeys.length : 0}
                    </td>
                    <td className="px-3 py-2">{item.currentQrBlockCount ?? 0}</td>
                    <td className="px-3 py-2">{formatAdminDate(item.updatedAt, language)}</td>
                    <td className="px-3 py-2">
                      <TemplateRowActions
                        item={item}
                        t={t}
                        variant="inline"
                        writeDisabled={impersonating}
                        onOpen={() => navigate(`/admin/prospection/letter-templates/${item.templateId}`)}
                        onDuplicate={() => setDuplicateTarget(item)}
                        onArchive={() => setArchiveTarget(item)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-3 md:hidden">
            {templates.map((item) => (
              <article
                key={item.templateId}
                className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              >
                <div className="min-w-0">
                  <p className="break-words text-base font-semibold leading-snug text-slate-900">
                    {item.name}
                  </p>
                  <span
                    className={`mt-2 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${adminProspectLetterTemplateStatusBadgeClass(item.status)}`}
                  >
                    {adminProspectLetterTemplateStatusLabel(item.status, t)}
                  </span>
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3">
                  <MobileCardField label={t('admin.prospection.letterTemplate.field.version')}>
                    {item.currentVersion ?? ADMIN_DATE_PLACEHOLDER}
                  </MobileCardField>
                  <MobileCardField label={t('admin.prospection.letterTemplate.field.variables')}>
                    {Array.isArray(item.currentPlaceholderKeys) ? item.currentPlaceholderKeys.length : 0}
                  </MobileCardField>
                  <MobileCardField label={t('admin.prospection.letterTemplate.field.qr')}>
                    {item.currentQrBlockCount ?? 0}
                  </MobileCardField>
                  <MobileCardField label={t('admin.prospection.letterTemplate.field.updatedAt')}>
                    {formatAdminDate(item.updatedAt, language)}
                  </MobileCardField>
                </dl>
                <div className="mt-4 border-t border-slate-100 pt-3">
                  <TemplateRowActions
                    item={item}
                    t={t}
                    variant="stacked"
                    writeDisabled={impersonating}
                    onOpen={() => navigate(`/admin/prospection/letter-templates/${item.templateId}`)}
                    onDuplicate={() => setDuplicateTarget(item)}
                    onArchive={() => setArchiveTarget(item)}
                  />
                </div>
              </article>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:justify-between sm:gap-3">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={tokenStack.length === 0 || status === 'paging'}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 sm:min-h-0 sm:px-3 sm:py-1.5 sm:font-medium"
            >
              {t('admin.prospection.letterTemplate.list.prev')}
            </button>
            <button
              type="button"
              onClick={handleNext}
              disabled={!nextPageToken || status === 'paging'}
              className="inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 sm:min-h-0 sm:px-3 sm:py-1.5 sm:font-medium"
            >
              {t('admin.prospection.letterTemplate.list.next')}
            </button>
          </div>
        </>
      ) : null}

      <AdminDuplicateLetterTemplateDialog
        open={!!duplicateTarget}
        sourceTemplate={duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        onSuccess={handleDuplicateSuccess}
      />
      <AdminArchiveLetterTemplateDialog
        open={!!archiveTarget}
        template={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onSuccess={handleArchiveSuccess}
      />
    </div>
  );
}

function MobileCardField({ label, children }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm font-medium text-slate-900">{children}</dd>
    </div>
  );
}

function TemplateRowActions({ item, t, variant = 'inline', writeDisabled, onOpen, onDuplicate, onArchive }) {
  const isArchived = item.status === 'archived';
  const stacked = variant === 'stacked';
  const openLabel = isArchived
    ? t('admin.prospection.letterTemplate.action.view')
    : t('admin.prospection.letterTemplate.action.open');

  return (
    <div className={stacked ? 'flex flex-col gap-2' : 'flex flex-wrap gap-2'}>
      <button
        type="button"
        onClick={onOpen}
        className={
          stacked
            ? 'inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-indigo-600 px-3 text-sm font-semibold text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500'
            : 'text-sm font-medium text-indigo-700 hover:text-indigo-900'
        }
      >
        {openLabel}
      </button>
      {!writeDisabled ? (
        <div className={stacked ? 'grid grid-cols-2 gap-2' : 'contents'}>
          <button
            type="button"
            onClick={onDuplicate}
            className={
              stacked
                ? `inline-flex min-h-11 items-center justify-center rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500${isArchived ? ' col-span-2' : ''}`
                : 'text-sm font-medium text-slate-700 hover:text-slate-900'
            }
          >
            {t('admin.prospection.letterTemplate.action.duplicate')}
          </button>
          {!isArchived ? (
            <button
              type="button"
              onClick={onArchive}
              className={
                stacked
                  ? 'inline-flex min-h-11 items-center justify-center rounded-lg border border-rose-200 bg-white px-3 text-sm font-semibold text-rose-700 hover:bg-rose-50 focus:outline-none focus:ring-2 focus:ring-rose-500'
                  : 'text-sm font-medium text-rose-700 hover:text-rose-900'
              }
            >
              {t('admin.prospection.letterTemplate.action.archive')}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
